import RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';

import roverS from './profiles/rover-s.json';
import type { RobotProfile } from './profiles/types';
import type { Command, Telemetry } from './protocol';
import { PROTOCOL_VERSION } from './protocol';

import { Channel } from './link/channel';
import { VideoChannel } from './link/video';
import { clonePreset, getPreset, LINK_PRESETS } from './link/presets';

import { SimWorld } from './sim/world';
import { Robot } from './sim/robot';
import { SafetyController } from './sim/safety';
import { buildCourse } from './scene/course';

import { CameraFeeds, InstructorView, VIEW_H, VIEW_W } from './render/renderer';
import { InputController } from './input/controls';
import { Hud, STATE_LABELS, STATE_LEVEL, type CamView } from './ui/hud';
import { Beeper } from './ui/sound';
import { APP_VERSION, fmt, SessionLog } from './log/session';
import { Rng } from './util/rng';

const STEP_MS = 1000 / 60;
const TELEMETRY_RATE_HZ = 10;
const SNAPSHOT_RATE_HZ = 10;
const MAP_ID = 'polygon-core';
const MAP_VERSION = '0.1';

async function boot(): Promise<void> {
  await RAPIER.init();

  const params = new URLSearchParams(location.search);
  const seed = Number(params.get('seed') ?? Math.floor(Math.random() * 1e9));
  const rng = new Rng(seed);

  const profile = roverS as unknown as RobotProfile;
  const world = new SimWorld();
  const course = buildCourse(world);
  const spawns = course.spawns;
  const robot = new Robot(world, profile, spawns[0].pos, spawns[0].heading);

  // --- модель связи ---
  let preset = clonePreset(getPreset(params.get('preset') ?? 'lte_good'));
  const cmdChannel = new Channel<Command>(preset.command, rng);
  const tmChannel = new Channel<Telemetry>(preset.telemetry, rng);
  const feeds = new CameraFeeds(profile.cameras.length);
  const videoChannels = profile.cameras.map(
    () => new VideoChannel({ ...preset.video }, rng, VIEW_W, VIEW_H),
  );

  const safety = new SafetyController(profile.failsafe.command_ttl_ms, profile.failsafe.link_loss_stop_ms);
  const input = new InputController(profile);
  input.bindKeyboard(window);
  const beeper = new Beeper();
  const instructorView = new InstructorView();

  const log = new SessionLog({
    app_version: APP_VERSION,
    protocol_version: PROTOCOL_VERSION,
    physics: `rapier3d-compat ${RAPIER.version()}`,
    map: MAP_ID,
    map_version: MAP_VERSION,
    profile: profile.id,
    profile_calibration: { version: profile.calibration.version, date: profile.calibration.date },
    link_preset: preset.id,
    seed,
    started_at: new Date().toISOString(),
    start_state: { pos: spawns[0].pos, heading_deg: spawns[0].heading },
  });

  // --- состояние сессии ---
  let simTime = 0;
  let cmdSeq = 0;
  let tmSeq = 0;
  let cmdAcc = 0;
  let tmAcc = 0;
  let snapAcc = 0;
  let lastTelemetry: Telemetry | null = null;
  let telemetryRxAt = -Infinity;
  let measuredRtt = 0;
  let primaryCam = profile.cameras[0].id;
  const camSwitchUntil = new Map<string, number>();
  const sentAt = new Map<number, number>();
  let instructorOn = false;
  /** Сколько чекпоинтов маршрута пройдено */
  let cpPassed = 0;
  let routeStart = 0;
  let routeDone = false;
  let renderFps = 0;
  const fpsWindow: number[] = [];
  let lastLevel: 'ok' | 'warn' | 'error' = 'ok';
  let lastCtl = { move: [0, 0] as [number, number], yaw: 0 };

  const app = document.getElementById('app')!;
  const hud = new Hud(app, profile, spawns, log, {
    onPreset: (id) => applyPreset(id),
    onEstop: () => sendDiscrete('estop'),
    onEstopRelease: () => sendDiscrete('estop_release'),
    onRespawn: (id) => {
      const s = spawns.find((x) => x.id === id) ?? spawns[0];
      teleport(s.id);
      log.note(simTime, `телепорт: ${s.name}`, 'info');
    },
    onResetRoute: () => {
      teleport('start');
      log.note(simTime, 'маршрут сброшен на старт', 'info');
    },
    onInstructor: (on) => {
      instructorOn = on;
      if (on) instructorView.attach(hud.instructorCanvas);
      log.note(simTime, `вид инструктора: ${on ? 'включён' : 'выключен'}`);
    },
    onKeyboard: (on) => input.setKeyboardEnabled(on),
    onDeadMan: (on) => {
      profile.control.dead_man_switch = on;
      log.note(simTime, `dead-man switch: ${on ? 'включён' : 'выключен'}`);
    },
    onCutVideo: () => {
      for (const v of videoChannels) v.cutFor(simTime, 3000);
      log.note(simTime, 'сценарий: обрыв видео на 3 с', 'warn');
      log.add({ type: 'link', t: simTime, preset: preset.id, detail: 'video_cut_3000ms' });
    },
    onCutCommands: () => {
      cmdChannel.config.down = true;
      const until = simTime + 2000;
      const timer = setInterval(() => {
        if (simTime >= until) {
          cmdChannel.config.down = false;
          clearInterval(timer);
          log.note(simTime, 'канал команд восстановлен', 'warn');
        }
      }, 50);
      log.note(simTime, 'сценарий: обрыв команд на 2 с', 'error');
      log.add({ type: 'link', t: simTime, preset: preset.id, detail: 'command_cut_2000ms' });
    },
    onPrimaryCamera: (id) => {
      if (id === primaryCam) return;
      primaryCam = id;
      // Реальное переключение камеры не мгновенно — поток пропадает на camera_switch_delay_ms
      const idx = profile.cameras.findIndex((c) => c.id === id);
      if (idx >= 0) videoChannels[idx].cutFor(simTime, profile.camera_switch_delay_ms);
      camSwitchUntil.set(id, simTime + profile.camera_switch_delay_ms);
      log.note(simTime, `основная камера: ${id}`);
    },
    onStick: (x, y) => input.setStick(x, y),
    onStickRelease: () => input.releaseStick(),
    onYaw: (v) => input.setYaw(v),
    onYawRelease: () => input.releaseYaw(),
    onDpad: (v, w) => input.setDpad(v, w),
    onDpadRelease: () => input.releaseDpad(),
  });

  log.note(0, `сессия начата · профиль ${profile.id} · seed ${seed}`);
  if (!profile.calibration.date) {
    log.note(0, 'профиль не калиброван по реальному роботу — допуск операторов по нему не производится', 'warn');
  }
  log.note(0, `пресет связи: ${preset.name}`);

  /** Телепорт к этапу: пройденными считаются все чекпоинты до него */
  function teleport(spawnId: string): void {
    const s = spawns.find((x) => x.id === spawnId) ?? spawns[0];
    robot.respawn(s.pos, s.heading);
    input.zero();
    const cp = course.checkpoints.find((c) => `cp${c.n}` === s.id);
    cpPassed = cp ? cp.n : 0;
    routeDone = false;
    routeStart = simTime;
    log.add({ type: 'event', t: simTime, kind: 'respawn', detail: s.id });
  }

  function applyPreset(id: string): void {
    preset = clonePreset(getPreset(id));
    cmdChannel.config = preset.command;
    tmChannel.config = preset.telemetry;
    for (const v of videoChannels) v.config = { ...preset.video };
    log.note(simTime, `пресет связи: ${preset.name}`, 'info');
    log.add({ type: 'link', t: simTime, preset: preset.id });
  }

  function sendDiscrete(action: 'estop' | 'estop_release'): void {
    // Кнопки тоже идут через канал: аварийный стоп доедет не мгновенно
    const cmd: Command = { type: 'discrete', action, ts: simTime, seq: ++cmdSeq };
    cmdChannel.send(cmd, simTime);
    sentAt.set(cmd.seq, simTime);
    log.add({ type: 'cmd', t: simTime, cmd });
    log.note(simTime, action === 'estop' ? 'оператор: АВАРИЙНЫЙ СТОП' : 'оператор: снятие аварийного стопа', 'error');
    if (action === 'estop') input.zero();
  }

  // ---------- фиксированный шаг физики и связи ----------

  function fixedStep(): void {
    simTime += STEP_MS;
    const dt = STEP_MS / 1000;

    // 1. Оператор: ввод → команда в канал с частотой command_rate_hz
    const ctl = input.update(STEP_MS);
    lastCtl = { move: ctl.move, yaw: ctl.yaw };
    cmdAcc += STEP_MS;
    const cmdPeriod = 1000 / profile.control.command_rate_hz;
    while (cmdAcc >= cmdPeriod) {
      cmdAcc -= cmdPeriod;
      const cmd: Command = {
        type: 'move',
        move: ctl.move,
        yaw: ctl.yaw,
        ts: simTime,
        seq: ++cmdSeq,
      };
      cmdChannel.send(cmd, simTime);
      sentAt.set(cmd.seq, simTime);
      if (sentAt.size > 600) {
        const oldest = sentAt.keys().next().value;
        if (oldest !== undefined) sentAt.delete(oldest);
      }
      log.add({ type: 'cmd', t: simTime, cmd });
    }

    // 2. Борт: что реально доехало → машина состояний → привод
    const incoming = cmdChannel.receive(simTime);
    const out = safety.update(simTime, incoming, cmdChannel, robot.state.speed);
    robot.update(simTime, dt, out);

    // 3. Физика и столкновения
    const contacts = world.step();
    for (const [h1, h2] of contacts) {
      if (h1 !== robot.collider.handle && h2 !== robot.collider.handle) continue;
      const what = world.nameOf(h1 === robot.collider.handle ? h2 : h1);
      if (what === 'земля') continue;
      const speed = robot.impactSpeed;
      if (speed < 0.12) continue;
      log.add({ type: 'collision', t: simTime, what, speed_ms: Math.round(speed * 100) / 100 });
      log.note(simTime, `столкновение: ${what}, ${speed.toFixed(2)} м/с`, 'error');
      beeper.play('alarm');
    }

    // 4. Маршрут: чекпоинты проходятся строго по порядку
    if (!routeDone && cpPassed < course.checkpoints.length) {
      const next = course.checkpoints[cpPassed];
      const dx = robot.state.pos.x - next.pos[0];
      const dz = robot.state.pos.z - next.pos[1];
      if (dx * dx + dz * dz <= next.radius * next.radius) {
        cpPassed++;
        if (cpPassed === 1) routeStart = simTime;
        log.add({ type: 'event', t: simTime, kind: 'checkpoint', detail: `${next.n} ${next.name}` });
        if (cpPassed === course.checkpoints.length) {
          routeDone = true;
          log.note(simTime, `МАРШРУТ ПРОЙДЕН за ${fmt(simTime - routeStart)}`, 'info');
          beeper.play('ok');
        } else {
          log.note(simTime, `чекпоинт ${next.n}: ${next.name}`, 'info');
          beeper.play('ok');
        }
      }
    }

    // Заезды на ступеньки — по ним видно, где робот работал на пределе профиля
    for (const s of robot.takeStepEvents()) {
      log.add({ type: 'event', t: s.t, kind: 'step_climb', detail: `${Math.round(s.height * 1000)} мм` });
      log.note(s.t, `заезд на ступеньку ${Math.round(s.height * 1000)} мм`, 'info');
    }

    // 5. События безопасности
    for (const ev of safety.takeEvents()) {
      if (ev.kind === 'state') {
        log.add({ type: 'state', t: ev.t, from: ev.from, to: ev.to, detail: ev.detail });
        const level = ev.to ? STATE_LEVEL[ev.to] : 'ok';
        log.note(
          ev.t,
          `${STATE_LABELS[ev.to!]}${ev.detail ? ' — ' + ev.detail : ''}`,
          level === 'ok' ? 'info' : level,
        );
        if (level !== lastLevel) {
          beeper.play(level === 'error' ? 'alarm' : level === 'warn' ? 'warn' : 'ok');
          lastLevel = level;
        }
      } else {
        log.add({ type: 'event', t: ev.t, kind: ev.kind, detail: ev.detail });
        if (ev.kind !== 'stale_command' || rng.chance(0.15)) {
          log.note(ev.t, `${ev.kind}: ${ev.detail ?? ''}`, 'warn');
        }
      }
    }

    // 6. Телеметрия вниз по своему каналу
    tmAcc += STEP_MS;
    const tmPeriod = 1000 / TELEMETRY_RATE_HZ;
    while (tmAcc >= tmPeriod) {
      tmAcc -= tmPeriod;
      const s = robot.state;
      const tm: Telemetry = {
        ts: simTime,
        seq: ++tmSeq,
        state: safety.state,
        speed_ms: Math.round(s.speed * 1000) / 1000,
        yaw_rate_dps: Math.round(s.yawRateDps * 10) / 10,
        heading_deg: Math.round(s.headingDeg * 10) / 10,
        pos: [
          Math.round(s.pos.x * 100) / 100,
          Math.round(s.pos.y * 100) / 100,
          Math.round(s.pos.z * 100) / 100,
        ],
        battery_pct: Math.round(s.batteryPct * 10) / 10,
        slope_deg: Math.round(s.slopeDeg * 10) / 10,
        surface: s.surface.name,
        camera: primaryCam,
        grounded: s.grounded,
        last_cmd_seq: safety.lastAcceptedSeq,
      };
      tmChannel.send(tm, simTime);
    }

    // 7. Оператор получает телеметрию — с задержкой, как всё остальное
    for (const tm of tmChannel.receive(simTime)) {
      if (lastTelemetry && tm.seq < lastTelemetry.seq) continue; // старый пакет
      lastTelemetry = tm;
      telemetryRxAt = simTime;
      const sent = sentAt.get(tm.last_cmd_seq);
      if (sent !== undefined) measuredRtt = measuredRtt * 0.7 + (simTime - sent) * 0.3;
    }

    // 8. Снапшоты в лог
    snapAcc += STEP_MS;
    const snapPeriod = 1000 / SNAPSHOT_RATE_HZ;
    while (snapAcc >= snapPeriod) {
      snapAcc -= snapPeriod;
      const s = robot.state;
      log.add({
        type: 'snap',
        t: simTime,
        tm: {
          ts: simTime,
          seq: tmSeq,
          state: safety.state,
          speed_ms: Math.round(s.speed * 1000) / 1000,
          yaw_rate_dps: Math.round(s.yawRateDps * 10) / 10,
          heading_deg: Math.round(s.headingDeg * 10) / 10,
          pos: [
            Math.round(s.pos.x * 1000) / 1000,
            Math.round(s.pos.y * 1000) / 1000,
            Math.round(s.pos.z * 1000) / 1000,
          ],
          battery_pct: Math.round(s.batteryPct * 10) / 10,
          slope_deg: Math.round(s.slopeDeg * 10) / 10,
          surface: s.surface.id,
          camera: primaryCam,
          grounded: s.grounded,
          last_cmd_seq: safety.lastAcceptedSeq,
        },
      });
    }
  }

  // ---------- рендер ----------

  function render(): void {
    const cams = profile.cameras.map((c) => robot.cameras.get(c.id)!);
    feeds.render(world.scene, cams);

    profile.cameras.forEach((c, i) => {
      const vc = videoChannels[i];
      vc.capture(simTime, feeds.canvas, feeds.region(i));
      const target = hud.videoCanvases.get(c.id)!;
      vc.present(simTime, target);
    });

    if (instructorOn) {
      const [w, h] = hud.instructorSize();
      instructorView.render(world.scene, robot.group, w, h);
    }
  }

  // ---------- главный цикл ----------

  let lastFrame = performance.now();
  let acc = 0;

  function frame(now: number): void {
    requestAnimationFrame(frame);
    const dtReal = Math.min(120, now - lastFrame);
    lastFrame = now;

    acc += dtReal;
    let steps = 0;
    while (acc >= STEP_MS && steps < 8) {
      fixedStep();
      acc -= STEP_MS;
      steps++;
    }

    render();

    fpsWindow.push(now);
    while (fpsWindow.length > 0 && now - fpsWindow[0] > 1000) fpsWindow.shift();
    renderFps = fpsWindow.length;

    const telemetryAge = lastTelemetry ? simTime - lastTelemetry.ts : Infinity;
    const silence = simTime - telemetryRxAt;
    const quality: 'ok' | 'degraded' | 'lost' =
      silence > 900 ? 'lost' : telemetryAge > 450 || safety.state === 'DEGRADED_LINK' ? 'degraded' : 'ok';

    const camViews: CamView[] = profile.cameras.map((c, i) => {
      const vc = videoChannels[i];
      return {
        id: c.id,
        label: c.label,
        fps: vc.fps,
        kbit: vc.bitrateKbit(),
        dropPct: vc.lossPct(),
        status: vc.status(simTime),
        ageMs: vc.frameAgeMs(simTime),
        primary: c.id === primaryCam,
        switching: (camSwitchUntil.get(c.id) ?? 0) > simTime,
      };
    });

    hud.update({
      now: simTime,
      presetName: preset.name,
      rttMs: measuredRtt,
      linkQuality: quality,
      telemetry: lastTelemetry,
      telemetryAgeMs: telemetryAge,
      state: lastTelemetry?.state ?? 'READY',
      cams: camViews,
      cmd: {
        move: lastCtl.move,
        yaw: lastCtl.yaw,
        seq: cmdSeq,
        rateHz: profile.control.command_rate_hz,
        source: input.lastSource,
      },
      route: {
        passed: cpPassed,
        total: course.checkpoints.length,
        next: routeDone ? '—' : (course.checkpoints[cpPassed]?.name ?? '—'),
        timeMs: cpPassed === 0 ? 0 : simTime - routeStart,
        done: routeDone,
      },
      renderFps,
      instructor: instructorOn,
    });
  }

  requestAnimationFrame(frame);

  // Периодический сброс лога в localStorage: падение вкладки не должно
  // стирать сессию (п. 16 ТЗ). Полноценное хранение — фаза 5.
  setInterval(() => {
    try {
      localStorage.setItem('robosim:last-session-size', String(log.size));
    } catch {
      /* приватный режим — не критично */
    }
  }, 5000);

  // Отладочный доступ из консоли: проверка критериев приёмки (п. 15 ТЗ),
  // ручные прогоны, замеры. Оператору не нужен и в UI не отражён.
  Object.assign(window as unknown as Record<string, unknown>, {
    robosim: {
      world,
      robot,
      safety,
      log,
      presets: LINK_PRESETS,
      THREE,
      channels: { command: cmdChannel, telemetry: tmChannel, video: videoChannels },
      input,
      course,
      /** Прогнать симуляцию на N мс без рендера — для проверок и замеров */
      step(ms: number) {
        const n = Math.round(ms / STEP_MS);
        for (let i = 0; i < n; i++) fixedStep();
      },
      get simTime() {
        return simTime;
      },
      get telemetryAgeMs() {
        return lastTelemetry ? simTime - lastTelemetry.ts : Infinity;
      },
      get rttMs() {
        return measuredRtt;
      },
    },
  });
}

void boot().catch((e: unknown) => {
  const app = document.getElementById('app');
  if (app) {
    app.innerHTML = `<pre style="padding:24px;color:#f85149;white-space:pre-wrap">Ошибка запуска симулятора:\n${String(e)}</pre>`;
  }
  console.error(e);
});
